import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAdmin as checkIsAdmin } from '@/lib/permissions';

type SystemRole = 'super_admin' | 'admin' | 'manager' | 'operator' | 'finance' | 'customer' | 'provider' | 'user' | 'viewer' | 'guest';

interface User {
  id: number;
  email: string;
  username: string;
  role: SystemRole;
  permissions: string[];
  isActive: boolean;
  authenticated: boolean;
  dbPermissions?: string[];
  dbPermissionsSource?: 'db' | 'fallback';
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  hasFetched: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  logoutMutation: any;
  refetch: () => void;
  registerMutation: any;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchUser = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/auth/me', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        if (userData && userData.authenticated === true) {
          setUser(userData);
        } else {
          setUser(null);
        }
      } else if (response.status === 401) {
        setUser(null);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  }, []);

  // Initial load and periodic refresh
  // UIUX-3: Removed console.warn to prevent console noise
  useEffect(() => {
    fetchUser().catch(() => {});
    
    const interval = setInterval(() => {
      fetchUser().catch(() => {});
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchUser]);

  const refetch = useCallback(() => {
    fetchUser();
  }, [fetchUser]);

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      setUser(data);
      // SECURITY: Auth tokens are managed via httpOnly cookies, not stored in localStorage
      fetchUser();
    },
    onError: (error) => {
      if (import.meta.env.DEV) {
        console.debug('Login error:', error);
      }
    }
  });

  const registerMutation = useMutation({
    mutationFn: async (userData: { username: string; email: string; password: string; fullName: string; company: string; phone: string }) => {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: userData.username,
          email: userData.email,
          password: userData.password,
          fullName: userData.fullName,
          company: userData.company,
          phone: userData.phone
        }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Registration failed');
      }
      
      return response.json();
    },
    onError: (error) => {
      // Silently handle errors to prevent console noise  
      if (import.meta.env.DEV) {
        console.debug('Registration error:', error);
      }
    }
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Logout failed');
      }
      
      return response.json();
    },
    onSuccess: () => {
      setUser(null);
      // SECURITY: Auth tokens in httpOnly cookies are cleared by the server
    },
    onError: (error) => {
      if (import.meta.env.DEV) {
        console.debug('Logout error:', error);
      }
    }
  });

  const login = async (email: string, password: string) => {
    await loginMutation.mutateAsync({ email, password });
  };

  const logout = async () => {
    await logoutMutation.mutateAsync();
  };

  const isAuthenticated = !!user?.authenticated;
  const isAdmin = user ? checkIsAdmin(user.role) : false;

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      hasFetched,
      isAuthenticated,
      isAdmin,
      login,
      logout,
      logoutMutation,
      refetch,
      registerMutation,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}